import {AttendanceReportExportPayload} from './attendanceReportExportTypes'
import {AttendanceDisplayMode} from './attendanceWizardTypes'
import {AttendanceVisualSummary} from './buildAttendanceVisualSummary'
import {formatAttendanceExportSummaryLines} from './formatAttendanceExportSummary'
import {AttendanceParsedContent} from './parseAttendanceAnswer'
import {
  mapRowsToExportColumns,
  pickStudentRecordExportColumns,
} from './pickStudentRecordExportColumns'
import {renderAttendanceSummaryChartPng} from './renderAttendanceSummaryChartPng'

export const EXPORT_COL = {
  STUDENT_ID: 'Student ID',
  STUDENT_NAME: 'Student Name',
  BRANCH: 'Branch',
  CLASS: 'Class',
  SECTION: 'Section',
  DATE_FROM: 'Date From',
  DATE_TO: 'Date To',
  PERIOD: 'Period',
  SCOPE: 'Scope',
  ATTENDANCE_MODE: 'Attendance Mode',
} as const

const IDENTITY_COLUMN_ORDER: string[] = [
  EXPORT_COL.STUDENT_ID,
  EXPORT_COL.STUDENT_NAME,
  EXPORT_COL.BRANCH,
  EXPORT_COL.CLASS,
  EXPORT_COL.SECTION,
  EXPORT_COL.DATE_FROM,
  EXPORT_COL.DATE_TO,
  EXPORT_COL.ATTENDANCE_MODE,
]

export type AttendanceExportContext = {
  studentId?: string
  studentName?: string
  branch?: string
  className?: string
  section?: string
  startDate?: string
  endDate?: string
  /** The window in the backend's own words ("this month", "this year so far"), for the answers that
   *  carry no dates to bound — an aggregate has a period but no rows to take one from. */
  periodLabel?: string
  /** Which branches and levels the figures cover, in the backend's own words. An exported PDF has
   *  no chat around it, so a report that names no scope is read as covering the whole school —
   *  the same failure the on-screen answer had before the scope line existed. */
  scopeLabel?: string
  attendanceModeLabel?: string
}

function normalizeColKey(col: string): string {
  return col.trim().toLowerCase().replace(/\s+/g, ' ')
}

function identityValues(ctx: AttendanceExportContext): Record<string, string> {
  return {
    [EXPORT_COL.STUDENT_ID]: ctx.studentId?.trim() ?? '',
    [EXPORT_COL.STUDENT_NAME]: ctx.studentName?.trim() ?? '',
    [EXPORT_COL.BRANCH]: ctx.branch?.trim() ?? '',
    [EXPORT_COL.CLASS]: ctx.className?.trim() ?? '',
    [EXPORT_COL.SECTION]: ctx.section?.trim() ?? '',
    [EXPORT_COL.DATE_FROM]: ctx.startDate?.trim() ?? '',
    [EXPORT_COL.DATE_TO]: ctx.endDate?.trim() ?? '',
    [EXPORT_COL.ATTENDANCE_MODE]: ctx.attendanceModeLabel?.trim() ?? '',
  }
}

function detailColumnsWithoutIdentity(
  columns: string[],
  identity: Record<string, string>
): string[] {
  const identityKeys = new Set(IDENTITY_COLUMN_ORDER.map(normalizeColKey))
  const identityValueByNorm = new Map<string, string>()
  for (const col of IDENTITY_COLUMN_ORDER) {
    identityValueByNorm.set(normalizeColKey(col), identity[col] ?? '')
  }

  return columns.filter((col) => {
    const norm = normalizeColKey(col)
    if (identityKeys.has(norm)) return false
    const idVal = identityValueByNorm.get(norm)
    if (idVal !== undefined && idVal !== '') return false
    return true
  })
}

export function buildExportMetaLines(ctx: AttendanceExportContext): string[] {
  const lines: string[] = []
  const push = (label: string, value?: string) => {
    const v = value?.trim()
    if (v) lines.push(`${label}: ${v}`)
  }

  push(EXPORT_COL.STUDENT_ID, ctx.studentId)
  push(EXPORT_COL.STUDENT_NAME, ctx.studentName)
  push(EXPORT_COL.BRANCH, ctx.branch)
  push(EXPORT_COL.CLASS, ctx.className)
  push(EXPORT_COL.SECTION, ctx.section)

  const from = ctx.startDate?.trim()
  const to = ctx.endDate?.trim()
  if (from && to) {
    lines.push(`Date range: ${from} – ${to}`)
  } else if (from) {
    push(EXPORT_COL.DATE_FROM, from)
  } else if (to) {
    push(EXPORT_COL.DATE_TO, to)
  }
  // Often the only window there is: an aggregate result carries no dates to bound, and a PDF that
  // states no period at all reads as though it covers everything.
  if (!from && !to) push(EXPORT_COL.PERIOD, ctx.periodLabel)

  // Deliberately after the window and before the mode: it qualifies the figures the same way the
  // date range does, and a reader scanning the header should meet both together.
  push(EXPORT_COL.SCOPE, ctx.scopeLabel)

  push(EXPORT_COL.ATTENDANCE_MODE, ctx.attendanceModeLabel)
  return lines
}

export type EnrichExportTableOptions = {
  /**
   * Optional leading columns per row (e.g. class roster). Report-level identity
   * (branch, dates, mode) belongs in metaLines only — not repeated in the table.
   */
  leadingColumns?: string[]
}

export function enrichExportTable(
  columns: string[],
  rows: Record<string, string>[],
  ctx: AttendanceExportContext,
  rowOverrides?: (row: Record<string, string>, index: number) => Partial<Record<string, string>>,
  options?: EnrichExportTableOptions
): {columns: string[]; rows: Record<string, string>[]} {
  if (!rows.length) return {columns: [], rows: []}

  const identity = identityValues(ctx)
  const detailCols = detailColumnsWithoutIdentity(columns, identity)
  const leading = options?.leadingColumns ?? []
  const exportColumns = [...leading, ...detailCols]

  const exportRows = rows.map((row, index) => {
    const overrides = rowOverrides?.(row, index) ?? {}
    const out: Record<string, string> = {}
    for (const col of exportColumns) {
      if (leading.includes(col)) {
        out[col] = overrides[col] ?? ''
      } else {
        out[col] = row[col] ?? ''
      }
    }
    return out
  })

  return {columns: exportColumns, rows: exportRows}
}

/** Class exports: one row per student — keep name/id/section in table only. */
export const CLASS_TABLE_LEADING_COLUMNS: string[] = [
  EXPORT_COL.STUDENT_ID,
  EXPORT_COL.STUDENT_NAME,
  EXPORT_COL.SECTION,
]

export function buildStudentAttendanceExportPayload(options: {
  title: string
  filename: string
  ctx: AttendanceExportContext
  content: AttendanceParsedContent
  summary?: AttendanceVisualSummary | null
  attendanceMode?: AttendanceDisplayMode
  extraMetaLines?: string[]
}): AttendanceReportExportPayload | null {
  const {title, filename, ctx, content, summary, attendanceMode, extraMetaLines = []} = options
  if (!content.columns.length || !content.rows.length) return null

  const metaLines = [...buildExportMetaLines(ctx), ...extraMetaLines.filter(Boolean)]
  const exportColumns = pickStudentRecordExportColumns(content.columns, attendanceMode)
  if (!exportColumns.length) return null

  const rows = mapRowsToExportColumns(content.rows, exportColumns)

  return {
    title,
    metaLines,
    summaryLines: formatAttendanceExportSummaryLines(summary),
    chartImageDataUrl: renderAttendanceSummaryChartPng(summary),
    columns: exportColumns,
    rows,
    filename,
  }
}

export function formatAttendanceModeLabel(
  mode: AttendanceDisplayMode | undefined,
  formatMessage: (id: {id: string}) => string,
  getModeLabelId: (mode: AttendanceDisplayMode) => string
): string | undefined {
  if (!mode) return undefined
  return formatMessage({id: getModeLabelId(mode)})
}
